import { faList, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Disclosure } from '@headlessui/react';
import { useCallback, useEffect, useMemo } from 'react';
import type { JSX } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FiltersBar from '@/components/FiltersBar';
import { Color } from '@/constants/color';
import itemStatuses from '@/constants/itemStatuses';
import taskSortOptions from '@/constants/taskSortOptions';
import ActiveFilters from '@/forms/TaskFilterForm/ActiveFilters';
import type { Props } from '@/forms/TaskFilterForm/types';
import useAPI from '@/hooks/useAPI';
import useStaff from '@/hooks/useStaff';
import WorkRepository from '@/repositories/WorkRepository';
import Accordion from '@/ui/components/Accordion/Accordion';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button/Button';
import DatetimePicker from '@/ui/components/Form/DatetimePicker';
import InputField from '@/ui/components/Form/InputField/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import MultiSelect from '@/ui/components/Form/MultiSelect/MultiSelect';
import type { SelectOption } from '@/ui/components/Form/MultiSelect/types';
import Spinner from '@/ui/components/Spinner';
import setStaffSelectValue from '@/utils/formatStaffAsSelectOption';
import setWorkGroupSelectValue from '@/utils/formatWorkGroupAsSelectOption';
import itemStatusName from '@/utils/itemStatusName';

const TaskFilterForm = ({ activeFilters, setFilters, setSort, sort }: Props): JSX.Element => {
  const workRepository = WorkRepository();

  const { data: staffUsers, isPending: isLoadingStaff } = useStaff({ withKustomerId: true });

  const { isPending: isLoadingBlueprints, data: blueprints } = useAPI({
    queryKey: ['getBlueprints'],
    queryFn: async () => {
      return await workRepository.listBlueprints();
    }
  });

  const { isPending: isLoadingWorkGroups, data: workGroups } = useAPI({
    queryKey: ['getWorkGroups'],
    queryFn: async () => {
      return await workRepository.getWorkGroups();
    }
  });

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch
  } = useForm({
    defaultValues: activeFilters,
    values: activeFilters
  });
  const values = watch();

  const applyFilters = useCallback((): void => {
    setFilters(values);
  }, [setFilters, values]);

  const staffSelectOptions = useMemo(
    () => staffUsers?.data?.map((staff) => setStaffSelectValue(staff)) ?? [],
    [staffUsers]
  );

  const blueprintSelectOptions = useMemo(
    () =>
      blueprints?.data?.map((blueprint) => ({
        label: blueprint.name,
        value: blueprint.id
      })) ?? [],
    [blueprints]
  );

  const workGroupSelectOptions = useMemo(
    () => workGroups?.data?.results.map((workGroup) => setWorkGroupSelectValue(workGroup)) ?? [],
    [workGroups]
  );

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.ordering]);

  const ApplyFiltersButton = () => (
    <Disclosure.Button as="div">
      <Button color={Color.INDIGO} disabled={isSubmitting} size="xs" type="submit">
        {isSubmitting ? <Spinner /> : 'Apply'}
      </Button>
    </Disclosure.Button>
  );

  const showActiveFilters = !!Object.entries(activeFilters).filter(
    (entry) => !['ordering', 'page'].includes(entry[0])
  ).length;

  const renderFilterSets = () => {
    return (
      <>
        <div className="mb-1">
          <Controller
            name="search"
            control={control}
            render={({ field }) => {
              return (
                <InputField
                  {...field}
                  id="search"
                  placeholder="Search"
                  type="text"
                  typeHelper="search"
                />
              );
            }}
          />
        </div>

        <div className="mb-1">
          <Accordion buttonText="Status">
            {errors.status?.message && <Alert level="error" message={errors.status.message} />}
            {itemStatuses.map((statusFilter) => {
              const id = `status-${statusFilter}`;
              const registerStatus = register('status');

              return (
                <Label key={id} htmlFor={id} reverse text={itemStatusName(statusFilter)}>
                  <input
                    {...registerStatus}
                    className="mr-1"
                    id={id}
                    type="checkbox"
                    value={statusFilter}
                  />
                </Label>
              );
            })}
            <ApplyFiltersButton />
          </Accordion>
        </div>

        <div className="mb-1">
          <Accordion buttonText="Task type">
            <InputGroup>
              {errors.blueprint_id?.message && (
                <Alert level="error" message={errors.blueprint_id.message} />
              )}

              {isLoadingBlueprints ? (
                <Spinner />
              ) : (
                <Controller
                  name="blueprint_id"
                  control={control}
                  render={({ field }) => {
                    const selectedBlueprintIds = Array.isArray(field.value)
                      ? field.value
                      : field.value
                        ? [field.value]
                        : [];

                    return (
                      <MultiSelect
                        label="Task type"
                        name="blueprint_id"
                        onChange={(options: SelectOption[]) =>
                          field.onChange(options.map((option) => option.value))
                        }
                        options={blueprintSelectOptions}
                        placeholder="Select Task Type"
                        selectedIcon={faList}
                        selectedOptions={blueprintSelectOptions.filter((blueprint) =>
                          selectedBlueprintIds.includes(blueprint.value)
                        )}
                      />
                    );
                  }}
                />
              )}
            </InputGroup>

            <ApplyFiltersButton />
          </Accordion>
        </div>

        <div className="mb-1 relative">
          <Accordion buttonText="Assignee">
            <InputGroup>
              {errors.assigned_user_id?.message && (
                <Alert level="error" message={errors.assigned_user_id.message} />
              )}

              {isLoadingStaff ? (
                <Spinner />
              ) : (
                <Controller
                  name="assigned_user_id"
                  control={control}
                  render={({ field }) => {
                    return (
                      <MultiSelect
                        label="Assigned User"
                        name="assigned_user_id"
                        onChange={(options: SelectOption[]) =>
                          field.onChange(options.map((option) => option.value))
                        }
                        options={staffSelectOptions}
                        placeholder="Select Assigned User"
                        selectedIcon={faUser}
                        selectedOptions={staffSelectOptions.filter((staff) =>
                          field.value?.includes(staff.value)
                        )}
                      />
                    );
                  }}
                />
              )}
            </InputGroup>

            <InputGroup>
              {errors.assigned_work_group_id?.message && (
                <Alert level="error" message={errors.assigned_work_group_id.message} />
              )}

              {isLoadingWorkGroups ? (
                <Spinner />
              ) : (
                <Controller
                  name="assigned_work_group_id"
                  control={control}
                  render={({ field }) => {
                    return (
                      <MultiSelect
                        label="Assigned Work Group"
                        name="assigned_work_group_id"
                        onChange={(options: SelectOption[]) =>
                          field.onChange(options.map((option) => option.value))
                        }
                        options={workGroupSelectOptions}
                        placeholder="Select Assigned Work Group"
                        selectedIcon={faUsers}
                        selectedOptions={workGroupSelectOptions.filter((workGroup) =>
                          field.value?.includes(workGroup.value)
                        )}
                      />
                    );
                  }}
                />
              )}
            </InputGroup>

            <ApplyFiltersButton />
          </Accordion>
        </div>

        <div className="mb-1">
          <Accordion buttonText="Due date">
            <InputGroup>
              {errors.due_at_start?.message && (
                <Alert level="error" message={errors.due_at_start.message} />
              )}
              <Controller
                name="due_at_start"
                control={control}
                render={({ field }) => {
                  return (
                    <DatetimePicker
                      onBlur={field.onBlur}
                      onChange={(val) => field.onChange(val ? val.toISOString() : val)}
                      label="From"
                      name={field.name}
                      showTimeSelect={false}
                      value={field.value}
                    />
                  );
                }}
              />
            </InputGroup>

            <InputGroup>
              {errors.due_at_end?.message && (
                <Alert level="error" message={errors.due_at_end.message} />
              )}
              <Controller
                name="due_at_end"
                control={control}
                render={({ field }) => {
                  return (
                    <DatetimePicker
                      onBlur={field.onBlur}
                      onChange={(val) => field.onChange(val ? val.toISOString() : val)}
                      label="To"
                      name={field.name}
                      showTimeSelect={false}
                      value={field.value}
                    />
                  );
                }}
              />
            </InputGroup>

            <ApplyFiltersButton />
          </Accordion>
        </div>
      </>
    );
  };

  return (
    <form className="relative" onSubmit={handleSubmit(applyFilters)}>
      <FiltersBar
        filterSets={renderFilterSets()}
        sort={{
          onChange: setSort,
          sortOptions: taskSortOptions,
          value: sort.value
        }}>
        {showActiveFilters && (
          <ActiveFilters
            filters={activeFilters}
            filterOptions={{
              blueprints: blueprints?.data ?? [],
              staffUsers: staffUsers?.data ?? [],
              statuses: itemStatuses,
              workGroups: workGroups?.data?.results ?? []
            }}
            setFilters={setFilters}
            values={values}
          />
        )}
      </FiltersBar>
    </form>
  );
};

export default TaskFilterForm;
